import { useEffect, useMemo, useReducer, useRef } from 'react';
import {
  SYNC,
  syncInit,
  syncTeardown,
  syncBroadcastState,
  syncSendCmd,
  syncScreenCount,
  type SyncRole,
  type SyncPeer,
} from './cockpitSync';
import { getScreenFromUrl } from './screens';

interface Opts {
  /** Réplique : applique l'état reçu du maître. */
  onState: (payload: unknown) => void;
  /** Maître : exécute une commande reçue d'une réplique. */
  onCmd?: (name: string, args: unknown) => void;
}

interface Result {
  role: SyncRole;
  isMaster: boolean;
  screenCount: number;
  available: boolean;
  screen: string;
  peers: [string, SyncPeer][];
  id: string;
  broadcastState: (payload: unknown) => void;
  sendCmd: (name: string, args?: unknown) => void;
}

/**
 * Intègre la couche de synchro multi-écrans (port de `sync.js`) à React.
 * Le composant se re-rend à chaque battement (~1 s) et à chaque état/commande
 * reçu, ce qui suffit à tenir à jour l'indicateur de rôle et l'affichage réplique.
 */
export function useCockpitSync({ onState, onCmd }: Opts): Result {
  const screen = useMemo(() => getScreenFromUrl(), []);
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const onCmdRef = useRef(onCmd);
  onCmdRef.current = onCmd;
  const [, force] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    syncInit({
      screen,
      onState: (p) => {
        onStateRef.current?.(p);
        force();
      },
      onCmd: (n, a) => {
        onCmdRef.current?.(n, a);
      },
      onPromotion: () => force(),
      onTick: () => force(),
    });
    return () => syncTeardown();
  }, [screen]);

  return {
    role: SYNC.role,
    isMaster: SYNC.role === 'master',
    screenCount: syncScreenCount(),
    available: SYNC.available,
    screen: SYNC.screen,
    peers: Array.from(SYNC.peers.entries()),
    id: SYNC.id,
    broadcastState: syncBroadcastState,
    sendCmd: syncSendCmd,
  };
}
